<template>
  <Teleport to="body">
    <div v-if="selectedEvent" class="event-panel-backdrop" @click="close" />
    <aside v-if="selectedEvent" class="event-panel">
      <!-- Header -->
      <div class="event-panel-header">
        <button class="event-panel-close" @click="close">Fermer ×</button>
      </div>

      <!-- Body -->
      <div class="event-panel-body">
        <!-- Breadcrumb -->
        <div class="event-panel-breadcrumb">
          {{ typeLabel }} · {{ sourceLabel }}
        </div>

        <!-- Title -->
        <h1 class="event-panel-title">{{ selectedEvent.name }}</h1>

        <!-- Tags -->
        <div class="event-panel-tags">
          <EventTypeBadge :type="selectedEvent.type" size="md" />
          <span v-if="isMultiDay" class="tag"
            >{{ selectedEvent.duration }}J</span
          >
        </div>

        <!-- CTA -->
        <div class="event-panel-cta">
          <a
            :href="selectedEvent.eventLink"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-primary"
            style="flex: 1; justify-content: center"
            @click="trackCta"
          >
            S'inscrire sur {{ sourceLabel }} →
          </a>
        </div>

        <hr class="event-panel-sep" />

        <!-- Details -->
        <div class="event-panel-details">
          <div class="event-panel-details-title">Détails</div>

          <div class="event-panel-detail-row">
            <span class="event-panel-detail-label">Dates</span>
            <span class="event-panel-detail-value">{{ formattedDates }}</span>
          </div>

          <div class="event-panel-detail-row">
            <span class="event-panel-detail-label">Lieu</span>
            <span class="event-panel-detail-value">{{ locationLabel }}</span>
          </div>

          <div class="event-panel-detail-row">
            <span class="event-panel-detail-label">Format</span>
            <span class="event-panel-detail-value">{{ formatLabel }}</span>
          </div>

          <div class="event-panel-detail-row">
            <span class="event-panel-detail-label">Source</span>
            <span class="event-panel-detail-value">{{ sourceLabel }}</span>
          </div>
        </div>
      </div>
    </aside>
  </Teleport>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';

const { selectedEvent, close } = useEventDetail();
const {
  proxy: { gtag },
} = useScriptGoogleAnalytics();

const isPanelOpen = computed(() => !!selectedEvent.value);
useEscapeKey(isPanelOpen, close);

const SOURCE_LABELS: Record<string, string> = {
  scoringFit: 'Scoring Fit',
  competitionCorner: 'Competition Corner',
  manual: 'Manuel',
  hyrox: 'Hyrox',
  maboxdecross: 'Ma Box de Cross',
};

const TYPE_LABELS: Record<string, string> = {
  online: 'Format en ligne',
  onSite: 'Format sur place',
};

const DURATION_LABELS: Record<string, string> = {
  '1': '1 jour',
  '2': '2 jours',
  '3': '3 jours',
};

const typeLabel = computed(
  () => TYPE_LABELS[selectedEvent.value?.type ?? ''] ?? '',
);

const sourceLabel = computed(
  () => SOURCE_LABELS[selectedEvent.value?.source ?? ''] ?? '',
);

const startDjs = computed(() => dayjs(selectedEvent.value?.startDate));
const endDjs = computed(() => dayjs(selectedEvent.value?.endDate));

const isMultiDay = computed(() => !startDjs.value.isSame(endDjs.value, 'day'));

const formattedDates = computed(() => {
  if (!selectedEvent.value) return '';
  const start = startDjs.value.format('DD.MM');
  if (!isMultiDay.value) return start;
  const end = endDjs.value.format('DD.MM');
  const days = endDjs.value.diff(startDjs.value, 'day') + 1;
  return `${start} → ${end} · ${days} jours`;
});

const locationLabel = computed(() => {
  const ev = selectedEvent.value;
  if (!ev) return '';
  if (ev.type === 'online') return 'En ligne';
  return ev.location || '—';
});

const formatLabel = computed(() => {
  const ev = selectedEvent.value;
  if (!ev) return '';
  const type = ev.type === 'online' ? 'En ligne' : 'Sur place';
  const dur = DURATION_LABELS[ev.duration] ?? '';
  return dur ? `${type} / ${dur}` : type;
});

const trackCta = () => {
  if (!selectedEvent.value) return;
  gtag('event', 'open_event', {
    event_category: 'engagement',
    event_label: selectedEvent.value.name,
    event_context: 'detail_panel',
  });
};
</script>
